import { Rol, type RolCanonico } from '../../domain/value-objects/Rol';
import { crearError, traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aNumero, aObjeto, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { env } from '../../config/env';

/** Usuario tal como se muestra en el listado de administración. */
export interface UsuarioAdmin {
  /** UUID de la cuenta. */
  id: string;
  nombre: string;
  email: string | null;
  telefono: string | null;
  rol: Rol;
  clienteId: number | null;
  clienteNombre: string | null;
  activo: boolean;
  bloqueado: boolean;
  debeCambiarClave: boolean;
  puedeUsarActuadores: boolean;
  puedeVerUbicaciones: boolean;
}

/** Alcance de un usuario más allá de su cliente principal. */
export interface AccesoUsuario {
  clienteIds: number[];
  areaIds: number[];
  puedeUsarActuadores: boolean;
}

export interface FiltrosUsuarios {
  busqueda?: string | null;
  rol?: RolCanonico | null;
  activo?: boolean | null;
  bloqueado?: boolean | null;
  clienteId?: number | null;
}

export interface DatosNuevoUsuario {
  email: string;
  claveInicial: string;
  nombre: string;
  rol: RolCanonico;
  clienteId: number | null;
  telefono: string | null;
}

export interface CambiosPerfil {
  nombre?: string;
  telefono?: string | null;
  rol?: RolCanonico;
  /** Sólo se envía si se quiere cambiar; `null` es un valor válido. */
  clienteId?: number | null;
}

export interface CambiosEstado {
  activo?: boolean;
  bloqueado?: boolean;
  debeCambiarClave?: boolean;
}

function aUsuario(fila: Record<string, unknown>): UsuarioAdmin {
  return {
    id: aTexto(fila.id) ?? aTexto(fila.user_id) ?? '',
    nombre: aTexto(fila.nombre) ?? 'Sin nombre',
    email: aTexto(fila.email),
    telefono: aTexto(fila.telefono),
    rol: Rol.from(aTexto(fila.rol)),
    clienteId: aNumero(fila.cliente_id),
    clienteNombre: aTexto(fila.cliente_nombre),
    activo: aBooleano(fila.activo) ?? true,
    bloqueado: aBooleano(fila.bloqueado) ?? false,
    debeCambiarClave: aBooleano(fila.debe_cambiar_clave) ?? false,
    puedeUsarActuadores: aBooleano(fila.puede_usar_actuadores) ?? true,
    puedeVerUbicaciones: aBooleano(fila.puede_ver_ubicaciones) ?? false,
  };
}

/**
 * Administración de usuarios.
 *
 * Nada de esto toca las tablas directamente: crear una cuenta, cambiar su rol
 * o bloquearla exige privilegios que el navegador no tiene ni debe tener. Todo
 * pasa por funciones del servidor que comprueban quién llama y qué alcance
 * tiene, y dejan traza de lo que hizo.
 */
export class RepositorioUsuarios {
  async listar(filtros: FiltrosUsuarios = {}): Promise<UsuarioAdmin[]> {
    const { data, error } = await supabase.rpc('v2_list_users_by_scope', {
      p_search: filtros.busqueda?.trim() || null,
      p_rol: filtros.rol ?? null,
      p_activo: filtros.activo ?? null,
      p_bloqueado: filtros.bloqueado ?? null,
      p_cliente_id: filtros.clienteId ?? null,
      p_limit: 500,
      p_offset: 0,
    });

    if (error) throw traducirErrorSupabase(error);

    const filas = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
    return filas.map(aUsuario).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  /** Ficha completa, incluidos los accesos adicionales. */
  async detalle(usuarioId: string): Promise<{ usuario: UsuarioAdmin; acceso: AccesoUsuario }> {
    const { data, error } = await supabase.rpc('v2_get_user_detail', { p_user_id: usuarioId });
    if (error) throw traducirErrorSupabase(error);

    const bruto = Array.isArray(data) ? data[0] : data;
    if (!bruto) throw crearError('NO_ENCONTRADO');

    const fila = bruto as Record<string, unknown>;

    return {
      usuario: aUsuario(fila),
      acceso: {
        clienteIds: this.aListaNumeros(fila.clientes_acceso),
        areaIds: this.aListaNumeros(fila.areas_acceso),
        puedeUsarActuadores: aBooleano(fila.puede_usar_actuadores) ?? true,
      },
    };
  }

  /**
   * Crea la cuenta.
   *
   * Va por una función del servidor porque dar de alta un usuario en el
   * sistema de autenticación requiere una clave de administración que no puede
   * viajar al navegador.
   */
  async crear(datos: DatosNuevoUsuario): Promise<void> {
    await this.llamarFuncion('v2-admin-create-user', {
      email: datos.email.trim().toLowerCase(),
      password_inicial: datos.claveInicial,
      nombre: datos.nombre.trim(),
      rol: datos.rol,
      cliente_id: datos.clienteId,
      telefono: datos.telefono,
    });
  }

  /**
   * Cambia nombre, teléfono, rol o cliente.
   *
   * El servidor distingue «no tocar el cliente» de «dejarlo sin cliente», así
   * que hay que decírselo con una bandera aparte: sin ella, un superadmin
   * perdería su alcance global sin querer.
   */
  async actualizarPerfil(usuarioId: string, cambios: CambiosPerfil): Promise<void> {
    const { error } = await supabase.rpc('v2_admin_update_user_profile', {
      p_user_id: usuarioId,
      p_nombre: cambios.nombre ?? null,
      p_telefono: cambios.telefono ?? null,
      p_rol: cambios.rol ?? null,
      p_set_cliente_id: cambios.clienteId !== undefined,
      p_cliente_id: cambios.clienteId ?? null,
    });

    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Activa, bloquea o fuerza el cambio de contraseña.
   * Sólo se envían los campos que cambian: el servidor ignora los ausentes.
   */
  async actualizarEstado(usuarioId: string, cambios: CambiosEstado): Promise<void> {
    const cuerpo: Record<string, unknown> = { user_id: usuarioId };

    if (cambios.activo !== undefined) cuerpo.activo = cambios.activo;
    if (cambios.bloqueado !== undefined) cuerpo.bloqueado = cambios.bloqueado;
    if (cambios.debeCambiarClave !== undefined) {
      cuerpo.debe_cambiar_clave = cambios.debeCambiarClave;
    }

    await this.llamarFuncion('v2-admin-update-user-status', cuerpo);
  }

  /**
   * Cambia si el usuario puede ver la ubicación de otras personas.
   *
   * Esta bandera vive en el perfil y no en los accesos, y se escribe directa
   * sobre la tabla: hay un disparador en la base que impide que un
   * administrador toque cualquier otra columna del perfil ajeno.
   */
  async cambiarVerUbicaciones(usuarioId: string, permitido: boolean): Promise<void> {
    const { error } = await supabase
      .from('perfiles')
      .update({ puede_ver_ubicaciones: permitido })
      .eq('id', usuarioId);

    if (error) throw traducirErrorSupabase(error);
  }

  /** Fija a qué clientes y áreas alcanza el usuario, más allá de su cliente. */
  async guardarAccesos(usuarioId: string, acceso: AccesoUsuario): Promise<void> {
    const { error } = await supabase.rpc('v2_admin_set_user_access', {
      p_user_id: usuarioId,
      p_cliente_ids: acceso.clienteIds,
      p_area_ids: acceso.areaIds,
      p_puede_usar_actuadores: acceso.puedeUsarActuadores,
    });

    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Llama a una función del servidor con la sesión del usuario.
   *
   * Se usa `fetch` en lugar del cliente de Supabase porque estas funciones
   * devuelven el motivo del rechazo en el cuerpo de la respuesta, y el cliente
   * lo descarta al convertir el error.
   */
  private async llamarFuncion(nombre: string, cuerpo: Record<string, unknown>): Promise<unknown> {
    const { data: sesion } = await supabase.auth.getSession();
    const token = sesion.session?.access_token;

    if (!token) throw crearError('SESION_EXPIRADA');

    let respuesta: Response;
    try {
      respuesta = await fetch(`${env.supabase.url}/functions/v1/${nombre}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: env.supabase.anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cuerpo),
      });
    } catch (e) {
      throw crearError('SIN_CONEXION', e);
    }

    const texto = await respuesta.text();
    const datos = texto ? aObjeto(texto) : {};

    if (!respuesta.ok) {
      const detalle = aTexto(datos.error) ?? aTexto(datos.message);

      if (respuesta.status === 401 || respuesta.status === 403) {
        throw crearError('SIN_PERMISO', datos, detalle ?? undefined);
      }
      throw crearError('DESCONOCIDO', datos, detalle ?? 'No se pudo completar la operación.');
    }

    return datos;
  }

  /** Los accesos llegan como lista de números o de objetos, según la versión. */
  private aListaNumeros(valor: unknown): number[] {
    if (!Array.isArray(valor)) return [];

    return valor
      .map((item) => {
        if (item !== null && typeof item === 'object') {
          const registro = item as Record<string, unknown>;
          return aNumero(registro.cliente_id ?? registro.area_id ?? registro.id);
        }
        return aNumero(item);
      })
      .filter((n): n is number => n !== null);
  }
}

export const repositorioUsuarios = new RepositorioUsuarios();
